import archiver from 'archiver';
import du from 'du';
import fs from 'fs';
import chalk from 'chalk';
import logger from '../../../utilities/logger';
import path from 'path';
import type { IPackageJson } from 'package-json-type';

export default async function zip({
  files,
  fileSizeLimit = 20,
  onError,
  onProgress,
  appPkgJson,
}: {
  files: ZipFile[];
  fileSizeLimit?: number;
  onError: (error: Error) => void;
  onProgress: (progress: ZipProgress & { percentage: number }) => void;
  appPkgJson?: IPackageJson;
}) {
  logger.debug({ files }, 'zip');
  const stream = archiver('zip');

  // good practice to catch warnings (ie stat failures and other non-blocking errors)
  stream.on('warning', (err: NodeJS.ErrnoException) => {
    if (err.code === 'ENOENT') {
      console.warn(err);
      logger.warn({ err }, 'zip: stream warning ENOENT');
    } else {
      // throw error
      onError(err);
    }
  });

  // good practice to catch this error explicitly
  stream.on('error', (err: Error) => {
    onError(err);
  });

  // Grab the file size and more
  const processedFiles = await Promise.all(
    files.map(async (file) => {
      if (file.content !== undefined && file.content !== null) {
        const buffer = Buffer.isBuffer(file.content)
          ? file.content
          : Buffer.from(file.content);

        return {
          ...file,
          contentBuffer: buffer,
          isDirectory: false,
          size: buffer.length,
          virtual: true,
        };
      }

      if (!file.from) {
        throw new Error(
          `Zip entry for ${file.to} must provide either a source path or in-memory content.`,
        );
      }

      const stats = fs.lstatSync(file.from);
      const isDirectory = stats.isDirectory();

      return {
        ...file,
        isDirectory,
        stats,
        size: isDirectory ? await du(file.from) : stats.size,
        virtual: false,
      };
    }),
  );

  const totalFileSize = processedFiles.reduce((sum, b) => sum + b.size, 0);
  // Does file size sum exceed limit?
  if (totalFileSize > fileSizeLimit * 1000000) {
    onError(
      new Error(
        [
          `\n\nYour app is larger than ${fileSizeLimit}MB. Your app is ${chalk.bold.red.underline(
            (totalFileSize / 1000000).toFixed(1) + 'MB',
          )}.\n`,
          `You may be including unnecessary files in your app(see the \`appPath\` and \`appFiles\` options).`,
          `If not, this limit can be raised by setting the \`uploadSizeLimit\` option.`,
          `Learn more at https://www.npmjs.com/package/@todesktop/cli#uploadsizelimit---optional-number`,
        ].join('\n'),
      ),
    );
    return stream;
  }

  // Add the files to the ZIP
  processedFiles.forEach((entry) => {
    const { from, isDirectory, to, virtual, mode } = entry;
    const stats = 'stats' in entry ? entry.stats : undefined;
    const contentBuffer =
      'contentBuffer' in entry ? entry.contentBuffer : undefined;
    if (virtual) {
      if (!contentBuffer) {
        throw new Error(`Virtual file ${to} must have contentBuffer defined`);
      }

      stream.append(contentBuffer, {
        name: to,
        mode: mode ?? 0o644,
      });
    } else if (isDirectory && from) {
      stream.directory(from, to);
    } else if (appPkgJson && to === path.join('app', 'package.json')) {
      stream.append(JSON.stringify(appPkgJson), {
        name: to,
      });
    } else if (from) {
      stream.file(from, { name: to, mode: mode ?? stats?.mode });
    }
  });

  // finalize the archive (ie we are done appending files but streams have to finish yet)
  // 'close', 'end' or 'finish' may be fired right after calling this method so register to them beforehand
  stream.finalize();

  stream.on('progress', (progress: ZipProgress) =>
    onProgress({
      ...progress,
      percentage: (progress.entries.processed / progress.entries.total) * 100,
    }),
  );

  return stream;
}

interface ZipFile {
  content?: Buffer | string;
  from?: string;
  mode?: number;
  to: string;
}

interface ZipProgress {
  entries: { processed: number; total: number };
  fs: { totalBytes: number };
}
